import { LOCAL_STORAGE_KEYS } from '@/constants'

import http from '@/$plugins/http'
import Vue from 'vue'

import base from '../base'
import IS from '../base/is'

export default {
  namespaced: true,
  state: base.createState(IS.state, {
    basket: {
      id: window.localStorage.getItem(LOCAL_STORAGE_KEYS.basket),
      itemCount: 0,
      lastAction: null
    },
    localConfigurations: []
  }),
  getters: base.createGetters(IS.getters, {
    get: state => state.basket || {},
    getId: state => window.localStorage.getItem(LOCAL_STORAGE_KEYS.basket),
    getItemCount: state => (state.basket && state.basket.itemCount) || 0,
    getLastAction: state => (state.basket && state.basket.lastAction) || {},
    getConfiguration: state => (state.basket && state.basket.configuration) || {},
    getValidationResult: state => (state.basket && state.basket.configurationValidationResult) || {},
    getStepValidationResult: state => sectionName => (((state.basket && state.basket.configurationValidationResult) || {}).results || []).find(r => r.sectionName === sectionName) || { isValid: false, errors: {} },
    getSummary: state => (state.basket && state.basket.priceDetail) || {},
    getLocalConfiguration: state => productId => state.localConfigurations.find(c => c.productId === productId)
  }),
  mutations: base.createMutations(IS.mutations, {
    set (state, { basket, merge, omitLastAction }) {
      if (!basket) {
        basket = {
          id: window.localStorage.getItem(LOCAL_STORAGE_KEYS.basket),
          itemCount: 0,
          lastAction: null
        }
      }
      if (omitLastAction) delete basket.lastAction

      if (merge) {
        const mergedBasket = Object.assign({}, state.basket || {}, basket)
        Vue.set(state, 'basket', mergedBasket)
      } else {
        Vue.set(state, 'basket', basket)
      }

      if (basket.id) {
        window.localStorage.setItem(LOCAL_STORAGE_KEYS.basket, basket.id)
      }
    },
    setLocalConfiguration (state, configuration = {}) {
      const updateIndex = state.localConfigurations.findIndex(c => c.productId === configuration.productId)
      const newIndex = state.localConfigurations.length
      const index = updateIndex >= 0 ? updateIndex : newIndex
      const base = state.localConfigurations[index] || {}

      Vue.set(state.localConfigurations, index, Object.assign(base, configuration))
    }
  }),
  actions: base.createActions(IS.actions, {
    getMinimal ({ state, getters, commit, dispatch, rootGetters }) {
      const isKey = 'B2bBasket/getMinimal'
      commit('setLoading', { key: isKey, loading: true, initial: state.basket.basketState === undefined })

      return new Promise((resolve, reject) => {
        http({
          method: 'get',
          url: `/order/api/B2bBasket/${getters.getId ? getters.getId : '00000000-0000-0000-0000-000000000000'}/small`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: true })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setLoading', { key: isKey, loading: false })
          })
      })
    },
    get ({ state, getters, commit, dispatch, rootGetters }) {
      const isKey = 'B2bBasket/get'
      commit('setLoading', { key: isKey, loading: true, initial: state.basket.basketState === undefined })

      return new Promise((resolve, reject) => {
        http({
          method: 'get',
          url: `/order/api/B2bBasket/${getters.getId ? getters.getId : '00000000-0000-0000-0000-000000000000'}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setLoading', { key: isKey, loading: false })
          })
      })
    },
    update ({ state, getters, commit, dispatch, rootGetters }, basketConfiguration = {}) {
      const isKey = 'B2bBasket/update'
      commit('setSending', { key: isKey, sending: true })

      return new Promise((resolve, reject) => {
        http({
          method: 'put',
          url: `/order/api/B2bBasket/${getters.getId ? getters.getId : '00000000-0000-0000-0000-000000000000'}/configuration`,
          data: basketConfiguration
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    },
    delete ({ state, getters, commit, dispatch, rootGetters }) {
      const isKey = 'B2bBasket/delete'
      commit('setSending', { key: isKey, sending: true })

      return new Promise((resolve, reject) => {
        http({
          method: 'delete',
          url: `/order/api/B2bBasket/${getters.getId ? getters.getId : '00000000-0000-0000-0000-000000000000'}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    },
    addItem ({ state, getters, commit, dispatch, rootGetters }, { productId = '', additionalConfigurations = null, quantity = 0, omitLastAction = false }) {
      const isKey = 'B2bBasket/addItem'
      commit('setLoading', { key: isKey, loading: true, initial: state.basket.basketState === undefined })

      const localConfiguration = state.localConfigurations.find(c => c.productId === productId) || {}

      if (additionalConfigurations) {
        localConfiguration.additionalConfigurations = additionalConfigurations
      }

      return new Promise((resolve, reject) => {
        http({
          method: 'put',
          url: `/order/api/B2bBasket/${getters.getId}/items/add/${quantity}/${productId}`,
          data: localConfiguration
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false, omitLastAction })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setLoading', { key: isKey, loading: false })
          })
      })
    },
    updateItem ({ state, getters, commit, dispatch, rootGetters }, { item = {}, omitLastAction = false }) {
      const isKey = 'B2bBasket/updateItem'
      commit('setSending', { key: isKey, sending: true })

      const basketItem = state.basket.items.find(i => i.basketItemId === item.id) || {}

      return new Promise((resolve, reject) => {
        http({
          method: 'put',
          url: `/order/api/B2bBasket/${getters.getId}/items/${item.id}/update/${item.quantity}`,
          data: basketItem.configuration
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false, omitLastAction })
            resolve(getters.get)
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    },
    deleteItem ({ state, getters, commit, dispatch, rootGetters }, { item = {}, omitLastAction = false }) {
      const isKey = 'B2bBasket/addItem'
      commit('setSending', { key: isKey, sending: true })

      return new Promise((resolve, reject) => {
        http({
          method: 'delete',
          url: `/order/api/B2bBasket/${getters.getId}/items/remove/${item.id}`
        })
          .then(response => {
            commit('set', { basket: response.data.result, merge: false, omitLastAction })
            resolve()
          })
          .catch(reject)
          .finally(() => {
            commit('setSending', { key: isKey, sending: false })
          })
      })
    }
  })
}
